import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { TrendingUp, Activity, DollarSign, Droplets } from 'lucide-react';
import { Badge } from './ui/badge';
import apiService from '../services/apiService';

const MarketOverview = () => {
  const [marketData, setMarketData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let intervalId;
    let staleTimeoutId;

    const hasStale = (data) =>
      data && typeof data === 'object' &&
      Object.values(data).some((f) => f && typeof f === 'object' && f.status === 'stale');

    const fetchMarketOverview = async () => {
      try {
        const data = await apiService.getMarketOverview();
        if (data && typeof data === 'object') {
          setMarketData(data);
          // If any row is stale, re-poll in 30s (backend backstop is refreshing it);
          // otherwise the regular 5-minute interval keeps things fresh.
          if (staleTimeoutId) { clearTimeout(staleTimeoutId); staleTimeoutId = null; }
          if (hasStale(data)) {
            staleTimeoutId = setTimeout(fetchMarketOverview, 30000);
          }
        } else {
          setMarketData(null);
        }
      } catch (error) {
        console.error('Error fetching market overview:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMarketOverview();
    // Cheap read from the store — poll every 5 minutes.
    intervalId = setInterval(fetchMarketOverview, 300000);
    return () => {
      clearInterval(intervalId);
      if (staleTimeoutId) clearTimeout(staleTimeoutId);
    };
  }, []);

  const formatMarketCap = (value) => {
    if (!value || isNaN(value)) return 'Unavailable';
    if (value >= 1e12) return `$${(value / 1e12).toFixed(1)}T`;
    if (value >= 1e9) return `$${(value / 1e9).toFixed(1)}B`;
    return `$${value.toFixed(2)}`;
  };

  // Show only the as-of time — never the source. Date-only sources render as a date (no misleading 00:00).
  const fmtAsOf = (asOf) => {
    if (!asOf) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(String(asOf))) {
      const d = new Date(String(asOf) + 'T00:00:00');
      return isNaN(d.getTime()) ? String(asOf) : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    }
    let d;
    if (/^\d+$/.test(String(asOf))) d = new Date(parseInt(asOf, 10) * 1000);
    else d = new Date(asOf);
    if (isNaN(d.getTime())) return String(asOf);
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const StaleDot = ({ status }) => status === 'stale' ? (
    <span title="Data older than expected — showing last good value"
      className="inline-block w-1.5 h-1.5 rounded-full bg-amber-400 ml-1 align-middle" data-testid="stale-marker" />
  ) : null;

  const AsOf = ({ asOf, testid }) => asOf ? (
    <div className="text-[10px] text-gray-400" title={`As of ${fmtAsOf(asOf)}`} data-testid={testid}>{fmtAsOf(asOf)}</div>
  ) : null;

  const getMarketStats = () => {
    if (!marketData) return [];
    const mc = (fig, title, icon) => (fig && fig.total_market_cap) ? {
      title, icon,
      value: formatMarketCap(fig.total_market_cap),
      change: `${(fig.change24h || 0) >= 0 ? '+' : ''}${(fig.change24h || 0).toFixed(1)}%`,
      trend: (fig.change24h || 0) >= 0 ? 'up' : 'down',
      asOf: fig.as_of, status: fig.status, unavailable: false,
    } : { title, icon, value: 'Unavailable', change: '', trend: 'neutral', asOf: '', status: 'unavailable', unavailable: true };

    const fg = marketData.fear_greed_index;
    // Fear & Greed daily change: exactly 0 shows "0.00" (never N/A when data exists).
    const fgChange = !fg ? '' : (fg.daily_change == null ? 'N/A' : (fg.daily_change === 0 ? '0.00' : `${fg.daily_change > 0 ? '+' : ''}${fg.daily_change}`));

    return [
      mc(marketData.crypto, 'Crypto Market Cap', DollarSign),
      mc(marketData.equities, 'US Equities MCap', TrendingUp),
      mc(marketData.commodities, 'Gold Market Cap', Activity),
      fg ? { title: 'Crypto Fear&Greed', value: fg.value, change: fgChange, trend: (fg.daily_change || 0) >= 0 ? 'up' : 'down', icon: Droplets, asOf: fg.as_of, status: fg.status, unavailable: false }
         : { title: 'Crypto Fear&Greed', value: 'Unavailable', change: '', trend: 'neutral', icon: Droplets, asOf: '', status: 'unavailable', unavailable: true },
    ];
  };

  const marketStats = getMarketStats();

  const fg = marketData?.fear_greed_index || null;
  const vix = marketData?.vix_index || null;
  const btc = marketData?.btc_dominance || null;
  const usd = marketData?.usd_index || null;
  const t2 = marketData?.treasury_2y || null;
  const t10 = marketData?.treasury_10y || null;

  if (loading) {
    return (
      <Card className="h-full flex flex-col bg-white border border-orange-200 shadow-lg">
        <CardContent className="flex-1 p-6 flex items-center justify-center">
          <div className="text-center">
            <Activity className="w-8 h-8 text-[#F7931A] animate-spin mx-auto mb-4" />
            <p className="text-gray-500">Loading market overview...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Reusable list row: label · value + change + stale dot · as-of underneath. Unavailable when no data.
  const MetricRow = ({ label, fig, upIsGood = true, unit = '', testid }) => {
    const trend = fig?.trend || 'neutral';
    const changeColor = trend === 'neutral' ? 'text-gray-500'
      : (trend === 'up') === upIsGood ? 'text-green-600' : 'text-red-500';
    return (
      <div className="border-t border-orange-100 pt-4" data-testid={testid}>
        <div className="flex items-center justify-between">
          <span className="text-gray-500 text-sm">{label}</span>
          <div className="text-right">
            {!fig ? (
              <span className="text-gray-400 text-sm" data-testid={`${testid}-unavailable`}>Unavailable</span>
            ) : (
              <>
                <div className="flex items-center justify-end space-x-2">
                  <span className="text-gray-900 font-semibold">{fig.value}{unit}<StaleDot status={fig.status} /></span>
                  {fig.change && fig.change !== 'N/A' && (
                    <span className={`text-xs font-medium ${changeColor}`}>{fig.change}</span>
                  )}
                </div>
                <AsOf asOf={fig.as_of} testid={`${testid}-asof`} />
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <Card className="h-full flex flex-col bg-white border border-orange-200 shadow-lg">
      <CardHeader className="border-b border-orange-100 pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-navy text-lg font-heading flex items-center space-x-2">
            <Activity className="w-5 h-5 text-[#F7931A]" />
            <span>Market Overview 24H</span>
          </CardTitle>
          <Badge variant="secondary" className="bg-[#F7931A] bg-opacity-10 text-[#F7931A] border border-[#F7931A] border-opacity-30 text-xs">
            <Activity className="w-3 h-3 mr-1" />
            24H
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="flex-1 space-y-4">
        {/* Market Stats Grid */}
        <div className="grid grid-cols-2 gap-4">
          {marketStats.length > 0 ? marketStats.map((stat, index) => (
            <div key={stat.title || index} className="space-y-1" data-testid={`stat-${index}`}>
              <div className="flex items-center space-x-1">
                <stat.icon className="w-4 h-4 text-gray-500" />
                <span className="text-gray-500 text-xs">{stat.title}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className={`font-semibold ${stat.unavailable ? 'text-gray-400 text-sm' : 'text-gray-900'}`}>
                  {stat.value}<StaleDot status={stat.status} />
                </span>
                {stat.change && (
                  <span className={`text-xs font-medium ${stat.trend === 'up' ? 'text-green-600' : 'text-red-500'}`}>{stat.change}</span>
                )}
              </div>
              <AsOf asOf={stat.asOf} testid={`stat-${index}-asof`} />
            </div>
          )) : (
            <div className="col-span-2 text-center py-4">
              <p className="text-gray-500 text-sm">Market data unavailable</p>
            </div>
          )}
        </div>

        {/* Fear & Greed Index (same stored alternative.me value as the Crypto Fear&Greed stat above) */}
        <div className="border-t border-orange-100 pt-4" data-testid="row-fear-greed">
          <div className="flex items-center justify-between">
            <span className="text-gray-500 text-sm">Fear&Greed Index</span>
            {fg ? (
              <div className="text-right">
                <Badge variant="secondary" className="bg-orange-100 text-orange-700 hover:bg-orange-200">
                  {fg.label} {fg.value}<StaleDot status={fg.status} />
                </Badge>
                <AsOf asOf={fg.as_of} testid="row-fear-greed-asof" />
              </div>
            ) : <span className="text-gray-400 text-sm">Unavailable</span>}
          </div>
        </div>

        {/* VIX: rising = red (fear), falling = green (calm) */}
        <MetricRow label="VIX Index" fig={vix} upIsGood={false} testid="row-vix" />
        <MetricRow label="BTC Dominance" fig={btc} upIsGood={true} unit="%" testid="row-btc" />
        <MetricRow label="US Dollar Index" fig={usd} upIsGood={true} testid="row-usd" />
        {/* Treasury yields: rising = red; change shown in basis points */}
        <MetricRow label="2Y Treasury" fig={t2} upIsGood={false} unit="%" testid="row-2y" />
        <MetricRow label="10Y Treasury" fig={t10} upIsGood={false} unit="%" testid="row-10y" />
      </CardContent>
    </Card>
  );
};

export default MarketOverview;
